/**
 * Light: how much of the sky reaches a cell, and how much of a смолоскип.
 *
 * Until now the world had no light of its own. The day/night cycle was one
 * colour multiplied over everything, which meant a cave a hundred blocks down
 * was exactly as bright at noon as the meadow above it - and a torch lit
 * nothing but itself.
 *
 * **Two fields, not one.** Skylight is what the sun would deliver here, and it
 * is scaled by the hour at draw time; block light comes off torches and does
 * not care what time it is. A cell keeps both and the renderer takes whichever
 * is greater. Adding them together here would mean a cave lit by a torch going
 * dark at dusk along with the field outside.
 *
 * **Skylight falls straight down for free.** A column of open air is lit all
 * the way to the floor at full strength; every step sideways, and every step
 * down once something has come between the cell and the sky, costs one. That
 * is what makes a cave mouth bright and its back wall black without anybody
 * having to place a light.
 *
 * Both fields live in one byte per cell - sky in the high nibble, block light
 * in the low one.
 *
 * **It works on flat indices and a prepared opacity map, not on the world.**
 * The first version asked the World for each cell as it walked, and a full
 * pass took 3.4 seconds - twenty times what generating the world costs - with
 * a 38 ms hitch on every block placed or broken. It was the same mistake as
 * the one that cost a second of world generation once before: a chunk lookup
 * per cell, several million times over. Neighbours are now index arithmetic
 * and opacity is one array read.
 *
 * Pure: two arrays and some arithmetic. No blocks, no chunks, no engine.
 */

/** Brightest a cell can be. Fifteen steps is enough to cross a room. */
export const MAX_LIGHT = 15;

/** How far a change can possibly reach, and so how much has to be redone. */
export const LIGHT_RADIUS = MAX_LIGHT + 1;

/**
 * How bright a смолоскип burns.
 *
 * Fourteen, not fifteen: full strength is what the open sky gives, and a
 * torch should light a room without making it read as daylight.
 */
export const TORCH_LIGHT = 14;

/**
 * How bright a stove burns.
 *
 * Less than a смолоскип: a firebox mouth is a small opening and a torch is
 * meant to stay the thing you carry into the dark. Enough to light the room
 * a хата's stove stands in, which is the point of having it at all.
 */
export const STOVE_LIGHT = 11;

/**
 * Лава, which is the brightest thing underground and knows it.
 *
 * Brighter than a torch: a pool of it should be visible from the far end of
 * a cave, so that it is a thing you see before you are standing in it.
 */
export const LAVA_LIGHT = 15;

/** A light source: where it is, and how bright. */
export interface Emitter {
  x: number; y: number; z: number; level: number;
}

/** The box a relight touched, so the caller knows what to redraw. */
export interface LitBox {
  x0: number; y0: number; z0: number;
  x1: number; y1: number; z1: number;
}

/**
 * The three questions anything outside asks about light.
 *
 * An interface rather than a class, because what answers them changed: it
 * was one array over the whole world and is now the chunks themselves. The
 * mesher and the readout do not care which, and should not have to.
 */
export interface LightReads {
  sky(x: number, y: number, z: number): number;
  block(x: number, y: number, z: number): number;
  packed(x: number, y: number, z: number): number;
  copyRow(
    out: Uint8Array, at: number, x0: number, y: number, z: number,
    count: number,
  ): void;
}

export class LightField {
  /** sky << 4 | block, one byte a cell. */
  private readonly data: Uint8Array;
  /**
   * The frontier, as flat indices.
   *
   * One array reused by every pass rather than a fresh one each time: a full
   * world fill queues millions of cells, and growing a plain array that far
   * costs more than the walk it is driving.
   */
  private queue: Int32Array;
  /**
   * What the box held before a relight, so afterwards we can say which cells
   * actually moved.
   *
   * The difference matters enormously: relighting a box is cheap, but telling
   * the renderer that everything in it changed is not. One block dug used to
   * mark twenty-seven chunks for rebuilding - twenty-seven GPU buffers thrown
   * away and made again, for a block whose light reached three of them.
   */
  private scratch = new Uint8Array(0);
  /** How much of `queue` is in use. An instance field so the walk's inner
   * step can grow it without a closure per neighbour. */
  private count = 0;

  readonly sizeX: number;
  readonly sizeY: number;
  readonly sizeZ: number;
  /** Steps between neighbours along each axis, in the flat array. */
  private readonly strideZ: number;
  private readonly strideY: number;

  constructor(sizeX: number, sizeY: number, sizeZ: number) {
    this.sizeX = sizeX;
    this.sizeY = sizeY;
    this.sizeZ = sizeZ;
    this.strideZ = sizeX;
    this.strideY = sizeX * sizeZ;
    this.data = new Uint8Array(sizeX * sizeY * sizeZ);
    this.queue = new Int32Array(1024);
  }

  private index(x: number, y: number, z: number): number {
    return y * this.strideY + z * this.strideZ + x;
  }

  private inside(x: number, y: number, z: number): boolean {
    return x >= 0 && y >= 0 && z >= 0 &&
      x < this.sizeX && y < this.sizeY && z < this.sizeZ;
  }

  /**
   * Sky reaching this cell, 0-15.
   *
   * Anything above the world reads as full daylight: a mesher looking one
   * block past the top must not find a black ceiling there. Everywhere else
   * outside is dark.
   */
  sky(x: number, y: number, z: number): number {
    if (y >= this.sizeY) return MAX_LIGHT;
    if (!this.inside(x, y, z)) return 0;
    return this.data[this.index(x, y, z)]! >> 4;
  }

  /**
   * Both halves at once, as the byte they are stored in.
   *
   * For the mesher, which wants a whole chunk's worth and does not care what
   * the halves mean. Asking through `sky` and `block` cost eleven thousand
   * method calls per chunk, on the main thread, every time a chunk was
   * redrawn.
   */
  packed(x: number, y: number, z: number): number {
    // Above the world is open daylight, and the mesher looks one block past
    // the top of it. Everywhere else outside is dark.
    if (y >= this.sizeY) return MAX_LIGHT << 4;
    if (!this.inside(x, y, z)) return 0;
    return this.data[this.index(x, y, z)]!;
  }

  /**
   * Copy a run of cells along x into `out`.
   *
   * The mesher reads whole rows, and a row inside the world is contiguous in
   * this array - so it is one `set` rather than eighteen bounds-checked
   * lookups.
   */
  copyRow(out: Uint8Array, at: number, x0: number, y: number, z: number, count: number): void {
    if (y >= this.sizeY) { out.fill(MAX_LIGHT << 4, at, at + count); return; }
    if (y < 0 || z < 0 || z >= this.sizeZ) { out.fill(0, at, at + count); return; }
    const lo = Math.max(0, x0);
    const hi = Math.min(this.sizeX, x0 + count);
    if (hi <= lo) { out.fill(0, at, at + count); return; }
    // The edges of the world, if the run hangs over them.
    if (lo > x0) out.fill(0, at, at + (lo - x0));
    if (hi < x0 + count) out.fill(0, at + (hi - x0), at + count);
    const row = y * this.strideY + z * this.strideZ;
    out.set(this.data.subarray(row + lo, row + hi), at + (lo - x0));
  }

  /** Torchlight reaching this cell, 0-15. */
  block(x: number, y: number, z: number): number {
    if (!this.inside(x, y, z)) return 0;
    return this.data[this.index(x, y, z)]! & 0x0f;
  }

  /**
   * Light the whole world from scratch.
   *
   * @param opaque one byte a cell, non-zero where light stops, in this
   * field's own layout.
   * @param emitters every torch in the world. Generation places none, so on
   * a fresh world this is empty.
   */
  computeAll(opaque: Uint8Array, emitters: readonly Emitter[]): void {
    this.data.fill(0);
    const whole: LitBox = {
      x0: 0, y0: 0, z0: 0,
      x1: this.sizeX, y1: this.sizeY, z1: this.sizeZ,
    };
    this.flood(opaque, emitters, whole, true);
    this.flood(opaque, emitters, whole, false);
  }

  /**
   * Redo the light around a change.
   *
   * The box is grown by the distance light can travel, cleared, and refilled -
   * seeded both from whatever is still lit on its boundary and from any
   * emitter inside it.
   *
   * Rebuilding a box is more work per edit than the usual trick of walking
   * outward un-lighting cells, and far less to get wrong: an un-lighting pass
   * that is subtly incomplete leaves lit cells hanging in the dark and nobody
   * notices for months.
   */
  relight(
    opaque: Uint8Array, emitters: readonly Emitter[],
    cx: number, cy: number, cz: number, radius = LIGHT_RADIUS,
  ): LitBox | null {
    return this.relightBox(opaque, emitters, {
      x0: cx - radius, y0: cy - radius, z0: cz - radius,
      x1: cx + radius + 1, y1: cy + radius + 1, z1: cz + radius + 1,
    });
  }

  /**
   * The same, over a box said outright rather than a cube around a cell.
   *
   * Digging the roof off a sealed shaft lets daylight fall the whole depth of
   * it, however deep that is, so the box that has to be redone is sometimes
   * tall and never wide. A single radius cannot say that without making the
   * box tall in all three directions, which is a hundred times the work for a
   * shape nothing needed.
   */
  relightBox(
    opaque: Uint8Array, emitters: readonly Emitter[], want: LitBox,
  ): LitBox | null {
    const box: LitBox = {
      x0: Math.max(0, want.x0),
      y0: Math.max(0, want.y0),
      z0: Math.max(0, want.z0),
      x1: Math.min(this.sizeX, want.x1),
      y1: Math.min(this.sizeY, want.y1),
      z1: Math.min(this.sizeZ, want.z1),
    };
    if (box.x0 >= box.x1 || box.y0 >= box.y1 || box.z0 >= box.z1) return null;

    // Keep what it was, so the caller can be told the truth afterwards.
    const w = box.x1 - box.x0;
    const h = box.y1 - box.y0;
    const d = box.z1 - box.z0;
    if (this.scratch.length < w * h * d) this.scratch = new Uint8Array(w * h * d);
    let at = 0;
    for (let y = box.y0; y < box.y1; y++) {
      for (let z = box.z0; z < box.z1; z++) {
        const row = y * this.strideY + z * this.strideZ;
        this.scratch.set(this.data.subarray(row + box.x0, row + box.x1), at);
        at += w;
        this.data.fill(0, row + box.x0, row + box.x1);
      }
    }

    this.flood(opaque, emitters, box, true);
    this.flood(opaque, emitters, box, false);

    // The bounding box of what actually moved. Usually far smaller than the
    // box that was recomputed, and often empty: digging in a place that was
    // already dark, or already lit, changes no light at all.
    let mx0 = Infinity, my0 = Infinity, mz0 = Infinity;
    let mx1 = -Infinity, my1 = -Infinity, mz1 = -Infinity;
    at = 0;
    for (let y = box.y0; y < box.y1; y++) {
      for (let z = box.z0; z < box.z1; z++) {
        const row = y * this.strideY + z * this.strideZ;
        for (let x = 0; x < w; x++) {
          if (this.data[row + box.x0 + x] === this.scratch[at + x]) continue;
          const wx = box.x0 + x;
          if (wx < mx0) mx0 = wx;
          if (wx > mx1) mx1 = wx;
          if (y < my0) my0 = y;
          if (y > my1) my1 = y;
          if (z < mz0) mz0 = z;
          if (z > mz1) mz1 = z;
        }
        at += w;
      }
    }
    if (mx1 < mx0) return null;
    return { x0: mx0, y0: my0, z0: mz0, x1: mx1 + 1, y1: my1 + 1, z1: mz1 + 1 };
  }

  /**
   * Take the light of every cell from somewhere else.
   *
   * A world that keeps its light per chunk has no field of its own to relight
   * in: it copies the neighbourhood into a scratch field, redoes the box in
   * the middle, and copies the difference back. This is the first half of
   * that, and the reason the cells around the box can be trusted as seeds -
   * they hold light that was worked out properly, not zeroes.
   */
  load(packed: Uint8Array): void {
    this.data.set(packed.subarray(0, this.data.length));
  }

  private push(i: number): void {
    if (this.count >= this.queue.length) {
      const bigger = new Int32Array(this.queue.length * 2);
      bigger.set(this.queue);
      this.queue = bigger;
    }
    this.queue[this.count++] = i;
  }

  /**
   * One flood fill, for either kind of light.
   *
   * `isSky` picks which nibble is written, where the seeds come from, and
   * whether going down is free. The two fills are otherwise the same walk, and
   * writing them out twice is how they end up disagreeing about a corner case.
   */
  private flood(
    opaque: Uint8Array, emitters: readonly Emitter[],
    box: LitBox, isSky: boolean,
  ): void {
    const shift = isSky ? 4 : 0;
    const keep = isSky ? 0x0f : 0xf0;
    const data = this.data;
    const { strideY, strideZ, sizeX, sizeY, sizeZ } = this;
    this.count = 0;

    const levelAt = (i: number): number =>
      shift === 4 ? data[i]! >> 4 : data[i]! & 0x0f;

    // Seeding takes coordinates because the boundary reads have to answer for
    // cells outside the field; the walk below is all flat indices.
    /**
     * @param throughSolid a light source sits in its own cell whether or not
     * that cell is see-through. A stove is a solid block with a fire in it;
     * refusing to put the light where the fire is would leave the room dark
     * around a visibly burning stove.
     */
    const seed = (
      x: number, y: number, z: number, level: number, throughSolid = false,
    ): void => {
      if (level <= 0) return;
      if (x < 0 || y < 0 || z < 0 || x >= sizeX || y >= sizeY || z >= sizeZ) return;
      const i = y * strideY + z * strideZ + x;
      if (!throughSolid && opaque[i] !== 0) return;
      if (levelAt(i) >= level) return;
      data[i] = (data[i]! & keep) | (level << shift);
      this.push(i);
    };

    if (isSky) {
      // The lid: daylight entering from above. `sky` answers MAX_LIGHT above
      // the top of the world, so a box that reaches it is seeded at full.
      for (let z = box.z0; z < box.z1; z++) {
        for (let x = box.x0; x < box.x1; x++) {
          seed(x, box.y1 - 1, z, this.sky(x, box.y1, z));
        }
      }
    } else {
      // The torches inside the box. Held as a list rather than found by
      // scanning: a full-world scan for something the player has placed four
      // of is millions of reads to find four cells.
      for (const e of emitters) {
        if (e.x < box.x0 || e.x >= box.x1) continue;
        if (e.y < box.y0 || e.y >= box.y1) continue;
        if (e.z < box.z0 || e.z >= box.z1) continue;
        seed(e.x, e.y, e.z, e.level, true);
      }
    }

    // The walls of the box, whichever kind of light: what is already lit just
    // outside comes in one step weaker. This is what lets a relit box inherit
    // the daylight, or the torchlight, streaming in from beyond it.
    const read = isSky
      ? (x: number, y: number, z: number): number => this.sky(x, y, z)
      : (x: number, y: number, z: number): number => this.block(x, y, z);
    for (let y = box.y0; y < box.y1; y++) {
      for (let z = box.z0; z < box.z1; z++) {
        seed(box.x0, y, z, read(box.x0 - 1, y, z) - 1);
        seed(box.x1 - 1, y, z, read(box.x1, y, z) - 1);
      }
      for (let x = box.x0; x < box.x1; x++) {
        seed(x, y, box.z0, read(x, y, box.z0 - 1) - 1);
        seed(x, y, box.z1 - 1, read(x, y, box.z1) - 1);
      }
    }
    for (let z = box.z0; z < box.z1; z++) {
      for (let x = box.x0; x < box.x1; x++) {
        seed(x, box.y0, z, read(x, box.y0 - 1, z) - 1);
        // The sky pass already seeded its lid from above at full strength.
        if (!isSky) seed(x, box.y1 - 1, z, read(x, box.y1, z) - 1);
      }
    }

    // The walk. Flat indices throughout: the only coordinate still needed is
    // x, to know whether a step sideways would wrap onto the next row.
    for (let head = 0; head < this.count; head++) {
      const i = this.queue[head]!;
      const level = levelAt(i);
      if (level <= 1) continue;

      const x = i % sizeX;
      const y = (i / strideY) | 0;
      const next = level - 1;

      // Straight down at full strength while the sky is still open above.
      // This one line is why a shaft is lit to its floor and a side passage
      // two steps off it is not.
      const down = isSky && level === MAX_LIGHT ? MAX_LIGHT : next;
      const z = ((i - y * strideY) / strideZ) | 0;
      if (y > 0) this.step(opaque, i - strideY, down, shift, keep);
      if (y < sizeY - 1) this.step(opaque, i + strideY, next, shift, keep);
      if (x > 0) this.step(opaque, i - 1, next, shift, keep);
      if (x < sizeX - 1) this.step(opaque, i + 1, next, shift, keep);
      if (z > 0) this.step(opaque, i - strideZ, next, shift, keep);
      if (z < sizeZ - 1) this.step(opaque, i + strideZ, next, shift, keep);
    }
  }

  private step(
    opaque: Uint8Array, i: number, level: number, shift: number, keep: number,
  ): void {
    if (opaque[i] !== 0) return;
    const have = shift === 4 ? this.data[i]! >> 4 : this.data[i]! & 0x0f;
    if (have >= level) return;
    this.data[i] = (this.data[i]! & keep) | (level << shift);
    this.push(i);
  }
}
